'use client';

import React, { useState } from 'react';
import { Save, Edit2, Phone, MessageSquare, Bell, CheckCircle } from 'lucide-react';

interface ContactData {
  primaryPhone: string;
  secondaryPhone: string;
  workEmail: string;
  personalEmail: string;
  preferredContact: string;
  textNotifications: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  scheduleAlerts: boolean;
  credentialAlerts: boolean;
  payrollAlerts: boolean;
  shiftReminders: boolean;
  linkedIn: string;
}

const INITIAL: ContactData = {
  primaryPhone: '(312) 555-0147',
  secondaryPhone: '(312) 555-0198',
  workEmail: 'm.rodriguez@medstaff.com',
  personalEmail: 'maria.rodriguez@gmail.com',
  preferredContact: 'Work Email',
  textNotifications: true,
  emailNotifications: true,
  pushNotifications: false,
  scheduleAlerts: true,
  credentialAlerts: true,
  payrollAlerts: true,
  shiftReminders: true,
  linkedIn: 'linkedin.com/in/mariarodriguez-rn',
};

export default function ContactDetails() {
  const [editing, setEditing] = useState(false);
  const [data, setData] = useState<ContactData>(INITIAL);
  const [draft, setDraft] = useState<ContactData>(INITIAL);
  const [saved, setSaved] = useState(false);

  const handleSave = () => {
    setData(draft);
    setEditing(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const toggle = (key: keyof ContactData) => {
    setDraft((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const textField = (label: string, key: keyof ContactData, type = 'text') => (
    <div>
      <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">{label}</label>
      {editing ? (
        <input
          type={type}
          value={draft[key] as string}
          onChange={(e) => setDraft({ ...draft, [key]: e.target.value })}
          className="mt-1 w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      ) : (
        <p className="mt-1 text-sm text-foreground py-2 px-3 bg-muted/40 rounded-lg">{data[key] as string || '—'}</p>
      )}
    </div>
  );

  const toggleRow = (label: string, key: keyof ContactData, description: string) => (
    <div className="flex items-center justify-between py-3 border-b border-border last:border-0">
      <div>
        <p className="text-sm font-500 text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <button
        onClick={() => toggle(key)}
        className={`relative w-10 h-5 rounded-full transition-colors flex-shrink-0 ${draft[key] ? 'bg-primary' : 'bg-border'}`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${draft[key] ? 'translate-x-5' : 'translate-x-0.5'}`}
        />
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      {saved && (
        <div className="flex items-center gap-2 bg-success/10 border border-success/20 text-success text-sm px-4 py-2.5 rounded-lg">
          <CheckCircle size={14} />
          Contact details saved successfully.
        </div>
      )}

      {/* Phone & Email */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Phone size={16} className="text-primary" />
            <h2 className="text-sm font-700 text-foreground">Phone & Email</h2>
          </div>
          {!editing ? (
            <button
              onClick={() => { setDraft(data); setEditing(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-xs font-500 text-foreground hover:bg-muted transition-colors"
            >
              <Edit2 size={12} />
              Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => { setDraft(data); setEditing(false); }}
                className="px-3 py-1.5 rounded-lg border border-border text-xs font-500 text-muted-foreground hover:bg-muted transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-500 hover:bg-primary/90 transition-colors"
              >
                <Save size={12} />
                Save
              </button>
            </div>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {textField('Primary Phone', 'primaryPhone', 'tel')}
          {textField('Secondary Phone', 'secondaryPhone', 'tel')}
          {textField('Work Email', 'workEmail', 'email')}
          {textField('Personal Email', 'personalEmail', 'email')}
        </div>
        <div className="mt-4">
          <label className="text-xs font-600 text-muted-foreground uppercase tracking-wide">Preferred Contact Method</label>
          {editing ? (
            <select
              value={draft.preferredContact}
              onChange={(e) => setDraft({ ...draft, preferredContact: e.target.value })}
              className="mt-1 w-full sm:w-64 bg-background border border-border rounded-lg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {['Work Email', 'Personal Email', 'Primary Phone', 'Text Message'].map((o) => <option key={o}>{o}</option>)}
            </select>
          ) : (
            <p className="mt-1 text-sm text-foreground py-2 px-3 bg-muted/40 rounded-lg w-fit">{data.preferredContact}</p>
          )}
        </div>
      </div>

      {/* Professional links */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare size={16} className="text-primary" />
          <h2 className="text-sm font-700 text-foreground">Professional Links</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {textField('LinkedIn Profile', 'linkedIn')}
        </div>
      </div>

      {/* Notification preferences */}
      <div className="bg-card border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Bell size={16} className="text-primary" />
          <h2 className="text-sm font-700 text-foreground">Notification Preferences</h2>
        </div>
        <p className="text-xs text-muted-foreground mb-4">Choose how and when you receive notifications from MedStaff.</p>

        <div className="mb-4">
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1">Channels</p>
          {toggleRow('Text / SMS', 'textNotifications', 'Receive notifications via text message')}
          {toggleRow('Email', 'emailNotifications', 'Receive notifications via email')}
          {toggleRow('Push Notifications', 'pushNotifications', 'Browser and mobile push alerts')}
        </div>

        <div>
          <p className="text-xs font-600 text-muted-foreground uppercase tracking-wide mb-1">Alert Types</p>
          {toggleRow('Schedule Changes', 'scheduleAlerts', 'Shift assignments, swaps, and cancellations')}
          {toggleRow('Credential Expiry', 'credentialAlerts', 'Reminders before credentials expire')}
          {toggleRow('Payroll Updates', 'payrollAlerts', 'Pay stub availability and payroll changes')}
          {toggleRow('Shift Reminders', 'shiftReminders', '2-hour reminder before each shift starts')}
        </div>

        {editing && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={handleSave}
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-500 hover:bg-primary/90 transition-colors"
            >
              <Save size={14} />
              Save Preferences
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
